import { useEffect, useRef, useState } from 'react';

/**
 * Hash routing, deliberately tiny. The app has five destinations and no need
 * for a router dependency; notification deep links set location.hash.
 */
export type Route =
  | { name: 'today' }
  | { name: 'memories' }
  /** replyTo: the snap this one answers, when it is a snap back. */
  | { name: 'camera'; replyTo?: string }
  | { name: 'chat'; focus?: string; reply?: boolean }
  | { name: 'search' }
  | { name: 'gallery' }
  | { name: 'you' }
  | { name: 'admin' }
  /** comments: opened on the post's comments, from a comment's notification. */
  | { name: 'daily'; day: string; uid?: string; comments?: boolean }
  /** The Sunday recap's collage, over the app: screens/Recap.tsx. */
  | { name: 'recap' };

/**
 * Exported for its own test. Routing is the one thing in the app that every
 * screen depends on and nothing else can check: a hash that parses wrong sends
 * somebody to the wrong screen silently, with no error anywhere.
 */
export function parse(hash: string): Route {
  const path = hash.replace(/^#\/?/, '');
  const [head, a, b, c] = path.split('/');

  switch (head) {
    case 'chat':
      // chat/<messageId> arrives from a search result and opens the thread
      // around that message rather than at the latest. A trailing /reply comes
      // from a daily and additionally quotes it in the composer, so a reply to
      // a photo lands in the one conversation rather than a thread of its own.
      return a ? { name: 'chat', focus: a, reply: b === 'reply' } : { name: 'chat' };
    case 'search':
      return { name: 'search' };
    case 'gallery':
      return { name: 'gallery' };
    case 'memories':
      return { name: 'memories' };
    case 'camera':
      // camera/reply/<messageId> is a snap back: the camera, sending to chat
      // as an answer to that snap.
      return a === 'reply' && b ? { name: 'camera', replyTo: b } : { name: 'camera' };
    case 'you':
      return { name: 'you' };
    case 'admin':
      return { name: 'admin' };
    case 'daily':
      // daily/<day>/<post>/comments, from a comment's notification, opens on
      // the comments rather than the photo.
      if (!a) return { name: 'today' };
      return c === 'comments' && b ? { name: 'daily', day: a, uid: b, comments: true } : { name: 'daily', day: a, uid: b };
    /*
     * Stories are gone; snaps in chat replaced them. A link to one still turns
     * up from a notification sent before they went, and the chat is where the
     * family's photos are now.
     */
    case 'story':
      return { name: 'chat' };
    /*
     * The app opens on the chat, which is where the camera is: the family talks
     * in photos there now, and a GiviSnap is one tab away with its dot on it.
     */
    case 'today':
      return { name: 'today' };
    case 'recap':
      return { name: 'recap' };
    default:
      return { name: 'chat' };
  }
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parse(window.location.hash));

  useEffect(() => {
    const onChange = () => setRoute(parse(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  return route;
}

export function go(to: string): void {
  window.location.hash = to.startsWith('#') ? to : `#/${to}`;
}

/*
 * The phone's back button, for the things that open over a screen.
 *
 * A sheet, a viewer or a confirm is React state, not a place, so it left no
 * entry in the history and back went straight past it -- off the screen, or
 * out of the app altogether when the screen was the first thing opened. Each
 * one now pushes an entry of its own while it is open, at the same address,
 * so back takes that entry away and the layer closes instead.
 *
 * An entry knows the layer it belongs to and the page load that made it. A
 * layer closed by its own button leaves its entry behind, and so does one a
 * reload forgot, so whenever the history settles on an entry whose layer is no
 * longer open it steps back past it. Without that, every sheet ever closed by
 * tapping would cost a dead press of back later.
 */
interface LayerEntry {
  layer: number;
  page: string;
}

const page = Math.random().toString(36).slice(2);
const layers = new Map<number, () => void>();
let lastLayer = 0;

function layerOf(state: unknown): number | null {
  const entry = state as Partial<LayerEntry> | null;
  return entry && typeof entry.layer === 'number' && entry.page === page ? entry.layer : null;
}

function isStale(state: unknown): boolean {
  const entry = state as Partial<LayerEntry> | null;
  if (!entry || typeof entry.layer !== 'number') return false;
  const layer = layerOf(state);
  return layer === null || !layers.has(layer);
}

/*
 * One step at a time. A sheet and the compose screen over it close together,
 * and both would step back from the same entry before either step landed --
 * then the first to land would step again, one further than anything opened.
 * Each landing settles afresh instead. The time is there in case a step lands
 * nowhere, which leaves no event to say so.
 */
let steppingSince = 0;
const STEP_GIVES_UP_MS = 1000;

function settle(): void {
  if (Date.now() - steppingSince < STEP_GIVES_UP_MS) return;
  if (!isStale(window.history.state)) return;
  steppingSince = Date.now();
  window.history.back();
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', (event) => {
    steppingSince = 0;
    // Everything opened after the entry we have landed on is behind us now,
    // newest first so a confirm goes before the sheet it sits on.
    const here = layerOf(event.state) ?? 0;
    const above = [...layers.keys()].filter((layer) => layer > here).sort((a, b) => b - a);
    for (const layer of above) {
      const close = layers.get(layer);
      layers.delete(layer);
      close?.();
    }
    settle();
  });
}

/**
 * Closes the layer when back is pressed while it is open. `close` is read at
 * the moment back arrives, so passing a fresh closure every render is fine.
 */
export function useBackCloses(close: () => void, open = true): void {
  const latest = useRef(close);
  latest.current = close;

  useEffect(() => {
    if (!open) return;
    const layer = ++lastLayer;
    layers.set(layer, () => latest.current());
    window.history.pushState({ layer, page } satisfies LayerEntry, '');
    return () => {
      layers.delete(layer);
      /*
       * Not at once: a layer closing as the next one opens -- tapping on
       * through somebody's snaps -- closes and opens in the same commit, and
       * the opening one pushes its entry just after this runs. A step back
       * asked for here, Chrome carries out from on top of that new entry, and
       * landing on this one's reads as back pressed, so the snap just opened
       * shut as well. Carter had to tap Monica's name for each of her five.
       * Looked at once the commit is done, the new entry is on top and open,
       * nothing is stale, and this one's is skipped on the next real back.
       */
      window.setTimeout(settle, 0);
    };
  }, [open]);
}

/*
 * The chat is home, so it is what back reaches last. Opened onto anything else
 * -- a GiviSnap from its notification, say -- the app had nothing behind that
 * screen, and back closed the app. So the chat is planted underneath first,
 * and the entry marked, so goBack() can tell when there is nowhere further back
 * inside the app to go.
 */
interface HomeEntry {
  home: true;
}

const isHome = (state: unknown) => (state as Partial<HomeEntry> | null)?.home === true;

export function plantHome(): void {
  const here = parse(window.location.hash);
  if (here.name === 'chat' && !here.focus) {
    window.history.replaceState({ home: true } satisfies HomeEntry, '');
    return;
  }
  const opened = window.location.href;
  window.history.replaceState({ home: true } satisfies HomeEntry, '', '#/chat');
  window.history.pushState(null, '', opened);
}

/**
 * Leaves a screen the way back would, so that back afterwards does not open it
 * again: closing with go() added an entry, and the screen just closed was the
 * next thing back showed. With nothing behind it in the app, it turns into
 * `fallback` where it stands.
 */
export function goBack(fallback: string): void {
  if (!isHome(window.history.state)) {
    window.history.back();
    return;
  }
  const was = window.location.href;
  window.history.replaceState({ home: true } satisfies HomeEntry, '', `#/${fallback}`);
  announce(was);
}

/** Goes to `to` in place of the screen here, which back then skips. */
export function swap(to: string): void {
  const was = window.location.href;
  window.history.replaceState(window.history.state, '', `#/${to}`);
  announce(was);
}

/**
 * replaceState moves the hash without saying so, so the screens are told here.
 * With the addresses a real one carries: one made bare has an empty oldURL,
 * and the error log's trail of screens threw on it every time.
 */
function announce(was: string): void {
  window.dispatchEvent(new HashChangeEvent('hashchange', { oldURL: was, newURL: window.location.href }));
}

/** The same, for a layer drawn inline under a condition, where a hook cannot go. */
export function BackCloses({ onBack }: { onBack: () => void }): null {
  useBackCloses(onBack);
  return null;
}
